import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { ComponentProps } from 'preact';
import { useMemo } from 'preact/hooks';

import ProtectedImg from '@/components/ProtectedImg';
import { getCardsQueryOptions } from '@/queries/cards';
import { AnyCard } from '@/queries/cards/types';
import { HabitatAnimal } from '@/queries/habitats/types';
import { useSelector } from '@/redux/helper';

interface AnimalGridProps extends ComponentProps<'ul'> {
  habitatId: string;
  animals: HabitatAnimal[] | undefined;
  onSelectAnimal?: (animalId: string, cards: AnyCard[]) => void;
  thumbnailClassName?: string;
  titleClassName?: string;
}

const AnimalGrid = ({
  habitatId,
  animals,
  onSelectAnimal,
  className,
  thumbnailClassName,
  titleClassName,
  ...props
}: AnimalGridProps) => {
  const isLogged = useSelector((state) => state.user.logged);
  const { data } = useQuery(getCardsQueryOptions(habitatId, 'meet'));

  const cardsByAnimalId = useMemo(() => {
    const map = data?.reduce((acc, card) => {
      if (!card.animalId) {
        return acc;
      }
      if (!acc.has(card.animalId)) {
        acc.set(card.animalId, []);
      }
      acc.get(card.animalId)?.push(card);
      return acc;
    }, new Map<string, NonNullable<typeof data>>());

    map?.forEach((cards) => {
      cards.sort((a, b) => a.index - b.index);
    });

    return map;
  }, [data]);

  const hasUnreadCardForAnimals = useMemo(() => {
    return animals?.map(
      (animal) => data?.some((card) => (isLogged ? card.animalId === animal._id && !card.isViewed : true)),
    );
  }, [animals, data, isLogged]);

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <ul className={clsx('preflight preflight-ul', className)} {...props}>
      {animals?.map((animal, index) => (
        <li key={animal._id}>
          <button
            type="button"
            className="preflight preflight-button relative flex flex-col items-center justify-center"
            onClick={() => {
              onSelectAnimal?.(animal._id, cardsByAnimalId?.get(animal._id) ?? []);
            }}
          >
            <ProtectedImg
              data-highlight={hasUnreadCardForAnimals?.[index]}
              src={animal.profileImage}
              alt={animal.name}
              className={clsx(
                'preflight preflight-img size-20 rounded-full border-4 border-[#F1F1F1] object-cover data-[highlight=true]:border-[#F3B62A]',
                thumbnailClassName,
              )}
            />
            <h4 className={clsx('preflight preflight-h4 mt-1 text-center text-base font-medium', titleClassName)}>
              {animal.name}
            </h4>
          </button>
        </li>
      ))}
    </ul>
  );
};

export default AnimalGrid;
